import React, {useRef} from 'react'
import {useTranslation} from 'react-i18next'
import {Modal, Collapse, Button, Typography, Input, Form} from 'antd'
import {WarningOutlined, PlusOutlined, CloseOutlined} from '@ant-design/icons'
import classNames from 'classnames'
import {observer} from 'mobx-react-lite'

import {useScreenType} from '@/hooks'
import {Icon, Status} from '@/components'
import {onboardingMessage} from '@/pages/onboarding/utils'
import {TagType} from '@/components/dumb/Tag/props'
import {RevertReason} from '@/stores/onboarding/models/onboarding'

import {AdditionalInfoModalProps} from './props'
import styles from './AdditionalInfoModal.scss'

const {Panel} = Collapse
const {Text, Paragraph} = Typography
const {TextArea} = Input

export const AdditionalInfoModal: React.FC<AdditionalInfoModalProps> = observer(
    ({open, onCancel, revertReasons, processKey, applicationProgressStore}) => {
        const {t} = useTranslation()
        const [form] = Form.useForm()
        const uploadRefs = useRef<{[key: string]: any}>({})
        const {isMobile} = useScreenType()

        React.useEffect(() => {
            applicationProgressStore.setAdditionalInfoForm(form)
        }, [form, applicationProgressStore])

        const handleAddComment = (reasonId: string) => {
            applicationProgressStore.toggleAdditionalInfoCommentField(reasonId, true)
        }

        const handleRemoveComment = (reasonId: string) => {
            applicationProgressStore.removeAdditionalInfoComment(reasonId)
        }

        const handleToggleFullComment = (reasonId: string) => {
            applicationProgressStore.toggleAdditionalInfoExpandedComment(reasonId)
        }

        const handleAttachFile = (reasonId: string) => {
            uploadRefs.current[reasonId]?.click()
        }

        const handleFileUpload = async (file: any, reasonId: string) => {
            if (!processKey) {
                onboardingMessage.error(t('AdditionalInfoModal.messages.processKeyNotAvailable'))
                return false
            }

            const success = await applicationProgressStore.uploadAdditionalInfoFile(
                file,
                reasonId,
                processKey
            )

            if (success) {
                onboardingMessage.success(t('AdditionalInfoModal.messages.fileUploadedSuccess'))
            } else {
                onboardingMessage.error(t('AdditionalInfoModal.messages.fileUploadFailed'))
            }

            return false
        }

        const handleRemoveFile = (reasonId: string, fileIndex: number) => {
            applicationProgressStore.removeAdditionalInfoUploadedFile(reasonId, fileIndex)
        }

        const handleSubmit = async () => {
            await applicationProgressStore.handleAdditionalInfoSubmit()
        }

        const handleConfirmSubmit = async () => {
            await applicationProgressStore.handleAdditionalInfoConfirmSubmit()
        }

        const handleBackToForm = () => {
            applicationProgressStore.setAdditionalInfoModalStage('form')
        }

        const handleClose = () => {
            applicationProgressStore.resetAdditionalInfoModalState()
            form.resetFields()
            onCancel()
        }

        const renderReasonPanel = (reason: RevertReason, index: number) => {
            const isIncomplete = !reason.answer
            const showComment = applicationProgressStore.additionalInfoShowCommentFields[reason.id]
            const reasonFiles =
                applicationProgressStore.additionalInfoUploadedFiles[reason.id] || []
            const isUploading = applicationProgressStore.additionalInfoUploading[reason.id] || false
            const hasError = applicationProgressStore.hasAdditionalInfoValidationError(reason.id)
            const isExpanded =
                applicationProgressStore.additionalInfoExpandedComments[reason.id] || false

            const getTruncatedComment = (comment: string) => {
                const maxLength = 120
                if (comment.length <= maxLength) return comment
                return comment.substring(0, maxLength) + '...'
            }

            const displayComment = isExpanded ? reason.comment : getTruncatedComment(reason.comment)
            const shouldShowToggle = reason.comment.length > 120

            return (
                <Panel
                    key={reason.id}
                    header={
                        <div className={styles.panelHeader}>
                            <Text strong>
                                {t('AdditionalInfoModal.reasonPanel.reasonNameLabel')} {reason.name}
                            </Text>
                            <div className={styles.headerIcons}>
                                {isIncomplete && (
                                    <Status
                                        status={t(
                                            'AdditionalInfoModal.reasonPanel.incompleteStatus'
                                        )}
                                        type={TagType.Default}
                                        iconType='close'
                                    />
                                )}
                            </div>
                        </div>
                    }
                >
                    <div className={styles.panelContent}>
                        <div className={styles.commentSection}>
                            <WarningOutlined
                                className={classNames(styles.commentWarningIcon, {
                                    [styles.commentWarningIconError]: hasError
                                })}
                            />
                            <Paragraph className={styles.comment}>{displayComment}</Paragraph>
                            {shouldShowToggle && (
                                <Button
                                    type='link'
                                    className={styles.showFullComment}
                                    onClick={() => handleToggleFullComment(reason.id)}
                                >
                                    {isExpanded
                                        ? t('AdditionalInfoModal.reasonPanel.showLess')
                                        : t('AdditionalInfoModal.reasonPanel.showFullComment')}
                                </Button>
                            )}
                        </div>
                        <Form.Item
                            name={['responses', reason.id, 'validation']}
                            className={styles.validationError}
                            rules={[
                                {
                                    validator: async (_, value) => {
                                        if (
                                            applicationProgressStore.hasAdditionalInfoValidationError(
                                                reason.id
                                            )
                                        ) {
                                            throw new Error(
                                                t(
                                                    'AdditionalInfoModal.messages.attachFileValidation'
                                                )
                                            )
                                        }
                                    }
                                }
                            ]}
                        >
                            <span style={{display: 'none'}} />
                        </Form.Item>

                        {reasonFiles.length > 0 && (
                            <div className={styles.uploadedFile}>
                                <span className={styles.fileName}>{reasonFiles[0].name}</span>
                                <Button
                                    type='link'
                                    size='small'
                                    icon={<CloseOutlined />}
                                    onClick={() => handleRemoveFile(reason.id, 0)}
                                    className={styles.removeFileButton}
                                />
                            </div>
                        )}
                        <div className={styles.actionButtons}>
                            {reasonFiles.length === 0 && (
                                <Button
                                    type='link'
                                    icon={<PlusOutlined />}
                                    className={styles.attachButton}
                                    onClick={() => handleAttachFile(reason.id)}
                                    loading={isUploading}
                                >
                                    {t('AdditionalInfoModal.reasonPanel.attachFile')}
                                </Button>
                            )}
                            {!showComment && (
                                <Button
                                    type='link'
                                    className={styles.addCommentButton}
                                    onClick={() => handleAddComment(reason.id)}
                                >
                                    {t('AdditionalInfoModal.reasonPanel.addComment')}
                                </Button>
                            )}
                        </div>

                        {showComment && (
                            <div className={styles.commentField}>
                                <Form.Item name={['responses', reason.id, 'answer']}>
                                    <TextArea
                                        placeholder={t(
                                            'AdditionalInfoModal.reasonPanel.commentPlaceholder'
                                        )}
                                        rows={2}
                                        className={classNames(styles.commentTextArea, {
                                            [styles.commentTextAreaMobile]: isMobile
                                        })}
                                    />
                                </Form.Item>
                                <Icon
                                    hoverable
                                    type={'trash'}
                                    onClick={() => handleRemoveComment(reason.id)}
                                />
                            </div>
                        )}

                        <Form.Item
                            name={['responses', reason.id, 'files']}
                            hidden
                            rules={[
                                {
                                    validator: async (_, value) => {
                                        if (
                                            applicationProgressStore.hasAdditionalInfoValidationError(
                                                reason.id
                                            )
                                        ) {
                                            throw new Error(
                                                t(
                                                    'AdditionalInfoModal.messages.attachFileValidation'
                                                )
                                            )
                                        }
                                    }
                                }
                            ]}
                        >
                            <input type='hidden' />
                        </Form.Item>

                        <input
                            ref={(el) => (uploadRefs.current[reason.id] = el)}
                            type='file'
                            accept='.pdf,.png,.jpg,.jpeg'
                            style={{display: 'none'}}
                            onChange={(e) => {
                                const file = e.target.files?.[0]
                                if (file) {
                                    handleFileUpload(file, reason.id)
                                    form.setFields([
                                        {
                                            name: ['responses', reason.id, 'files'],
                                            errors: []
                                        },
                                        {
                                            name: ['responses', reason.id, 'answer'],
                                            errors: []
                                        }
                                    ])
                                }
                                e.target.value = ''
                            }}
                        />
                    </div>
                </Panel>
            )
        }

        const renderFormStage = () => (
            <div className={styles.modalContent}>
                <Form form={form} layout='vertical' onFinish={handleSubmit} preserve={false}>
                    <Collapse
                        expandIconPosition={'end'}
                        ghost
                        bordered={false}
                        className={styles.reasonsCollapse}
                        defaultActiveKey={revertReasons.map((reason) => reason.id)}
                    >
                        {revertReasons.map((reason, index) => renderReasonPanel(reason, index))}
                    </Collapse>
                </Form>
            </div>
        )

        const renderConfirmStage = () => (
            <div className={styles.modalContent}>
                <div className={styles.confirmContent}>
                    <div className={styles.warningIcon}>
                        <img
                            src={require('~/assets/img/signUp/review_submitting.svg')}
                            alt={t('AdditionalInfoModal.confirmStage.altText')}
                        />
                    </div>
                    <Typography.Title level={4} className={styles.confirmTitle}>
                        {t('AdditionalInfoModal.confirmStage.title')}
                    </Typography.Title>
                    <Typography.Text type='secondary' className={styles.confirmDescription}>
                        {t('AdditionalInfoModal.confirmStage.description')}
                    </Typography.Text>
                </div>
            </div>
        )

        const renderSuccessStage = () => (
            <div className={styles.modalContent}>
                <div className={styles.successContent}>
                    <div className={styles.successIcon}>
                        <img
                            src={require('~/assets/img/signUp/onboarding_track_modal_image.svg')}
                            alt={t('AdditionalInfoModal.successStage.altText')}
                        />
                    </div>
                    <Typography.Title level={4} className={styles.successTitle}>
                        {t('AdditionalInfoModal.successStage.title')}
                    </Typography.Title>
                    <Typography.Text type='secondary' className={styles.successDescription}>
                        {t('AdditionalInfoModal.successStage.description')}
                    </Typography.Text>
                </div>
            </div>
        )

        const getModalTitle = () => {
            switch (applicationProgressStore.additionalInfoModalStage) {
                case 'form':
                    return t('AdditionalInfoModal.titles.form')
                case 'confirm':
                    return t('AdditionalInfoModal.titles.confirm')
                case 'success':
                    return t('AdditionalInfoModal.titles.success')
                default:
                    return t('AdditionalInfoModal.titles.default')
            }
        }

        const getModalFooter = () => {
            switch (applicationProgressStore.additionalInfoModalStage) {
                case 'form':
                    return (
                        <div className={styles.modalFooter}>
                            <Button onClick={handleClose}>
                                {t('AdditionalInfoModal.buttons.cancel')}
                            </Button>
                            <Button
                                type='primary'
                                onClick={handleSubmit}
                                loading={applicationProgressStore.additionalInfoSubmitting}
                            >
                                {t('AdditionalInfoModal.buttons.continue')}
                            </Button>
                        </div>
                    )
                case 'confirm':
                    return (
                        <div className={styles.modalFooter}>
                            <Button onClick={handleBackToForm}>
                                {t('AdditionalInfoModal.buttons.goBack')}
                            </Button>
                            <Button
                                type='primary'
                                onClick={handleConfirmSubmit}
                                loading={applicationProgressStore.additionalInfoSubmitting}
                            >
                                {t('AdditionalInfoModal.buttons.submit')}
                            </Button>
                        </div>
                    )
                case 'success':
                    return (
                        <div className={styles.modalFooter}>
                            <Button type='primary' onClick={handleClose}>
                                {t('AdditionalInfoModal.buttons.close')}
                            </Button>
                        </div>
                    )
                default:
                    return null
            }
        }

        const getModalContent = () => {
            switch (applicationProgressStore.additionalInfoModalStage) {
                case 'form':
                    return renderFormStage()
                case 'confirm':
                    return renderConfirmStage()
                case 'success':
                    return renderSuccessStage()
                default:
                    return renderFormStage()
            }
        }

        return (
            <Modal
                title={getModalTitle()}
                open={open}
                onCancel={handleClose}
                footer={getModalFooter()}
                width={600}
                className={styles.additionalInfoModal}
                closable={applicationProgressStore.additionalInfoModalStage !== 'success'}
            >
                {getModalContent()}
            </Modal>
        )
    }
)
